/**
 * Checks on one invoice at the moment it is reviewed (spec §4.4, D6).
 *
 * The project header flags an overrun against the campaign's agreed cost. This
 * is the other end of the same idea: the console is where a business user
 * actually approves money, and approving was a single click from a menu with
 * nothing in the way. The agreed price was on the row in 11px grey text, which
 * is not a check — it is a hope that someone looked.
 *
 * Nothing here blocks. A warning interrupts once, names what it found, and the
 * reviewer decides — including on a bulk move, where the old confirm said only
 * how many invoices were about to change.
 *
 * Pure.
 */

const FORWARD = 'forward';

export const FLAG = {
  OVER_AGREED: 'over_agreed',
  NO_AGREED: 'no_agreed',
  NO_PAYOUT: 'no_payout',
  UNVERIFIED_SUBMITTER: 'unverified_submitter',
  NEGATIVE_MARGIN: 'negative_margin',
};

function amount(value) {
  const parsed = typeof value === 'number' ? value : parseFloat(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

const money = (value) => `₹${Math.round(value).toLocaleString('en-IN')}`;

/**
 * Whether a transition moves the invoice further towards being paid.
 *
 * `next_statuses` entries carry `direction` as `forward`, `back` or `reject`.
 * Sending an invoice back or rejecting it needs no warning — those are the
 * correct response to a problem, not a risk.
 */
export function isForward(option) {
  if (!option) return false;
  if (typeof option === 'string') return option !== 'rejected';
  return option.direction === undefined ? option.value !== 'rejected' : option.direction === FORWARD;
}

/** `[{id, severity, label, detail}]`, warnings before notes. */
export function invoiceFlags(invoice) {
  const row = invoice || {};
  // Nothing to judge: an absent row is not an invoice with problems.
  if (!row.id && amount(row.amount) <= 0) return [];
  const staff = row.staff || {};
  const raised = amount(row.amount);
  const agreed = amount(row.agreed_amount);

  const warnings = [];
  const notes = [];

  if (agreed > 0 && raised > agreed) {
    warnings.push({
      id: FLAG.OVER_AGREED,
      severity: 'warn',
      label: 'Above the agreed price',
      detail: `Raised ${money(raised)} against ${money(agreed)} agreed — over by ${money(
        raised - agreed,
      )}.`,
    });
  }

  if (!staff.payout) {
    warnings.push({
      id: FLAG.NO_PAYOUT,
      severity: 'warn',
      label: 'No payout details',
      detail: 'Nothing on this invoice says where to pay it. Finance cannot action it.',
    });
  }

  if (staff.submitter && staff.submitter.matched === false) {
    warnings.push({
      id: FLAG.UNVERIFIED_SUBMITTER,
      severity: 'warn',
      label: 'Submitter not verified',
      detail: `Raised through an exception link by ${
        staff.submitter.name || 'someone'
      }, who could not be matched to the creator. ${staff.submitter.match_note || ''}`.trim(),
    });
  }

  if (staff.margin !== null && staff.margin !== undefined && amount(staff.margin) < 0) {
    warnings.push({
      id: FLAG.NEGATIVE_MARGIN,
      severity: 'warn',
      label: 'Costs more than it bills',
      detail: `This line bills the client ${money(
        amount(staff.client_billing),
      )} and pays out ${money(raised)}.`,
    });
  }

  // Common today, because the price lock is new: most invoices have no agreed
  // figure at all. Worth saying, not worth stopping someone over.
  if (agreed <= 0 && raised > 0) {
    notes.push({
      id: FLAG.NO_AGREED,
      severity: 'info',
      label: 'No agreed price',
      detail: 'No price was locked with this creator, so there is nothing to check against.',
    });
  }

  return [...warnings, ...notes];
}

/**
 * What a status move is about to do, and what deserves a look first.
 *
 * `needsReview` is true only for a forward move carrying at least one warning,
 * so sending something back, rejecting it, or moving clean invoices stays a
 * single click.
 */
export function reviewOf(invoices, option) {
  const list = Array.isArray(invoices) ? invoices : [];
  const flagged = [];
  const counts = {};
  let warnings = 0;

  list.forEach((invoice) => {
    const flags = invoiceFlags(invoice);
    if (flags.length === 0) return;
    flags.forEach((flag) => {
      counts[flag.id] = (counts[flag.id] || 0) + 1;
      if (flag.severity === 'warn') warnings += 1;
    });
    flagged.push({
      id: invoice.id,
      name: invoice.influencer_name || invoice.vendor_name || 'Unnamed',
      campaign: invoice.campaign_name || '',
      flags,
    });
  });

  return {
    total: list.length,
    flagged,
    counts,
    needsReview: warnings > 0 && isForward(option),
  };
}
